const MIN_HEIGHT = 200;
/** Gap between the flyout and its trigger / the nearest viewport edge. */
const EDGE_GAP = 8;

export type FlyoutPos = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

type FlyoutPosOptions = {
  /** Minimum width for the flyout (defaults to trigger width). */
  minWidth?: number;
  /**
   * Extra clearance subtracted from `maxHeight` so the flyout doesn't kiss
   * the viewport edges. The trigger-to-flyout gap (`EDGE_GAP`) is unchanged
   * — this only affects how tall the flyout is allowed to grow.
   */
  safetyMargin?: number;
};

/**
 * Computes the optimal position for a flyout relative to a trigger element.
 *
 *  - Opens downward when there's enough space below the trigger.
 *  - Flips upward when there's more space above and at least `MIN_HEIGHT`
 *    of room there.
 *  - Falls back to a centered position when neither direction has enough
 *    space (small viewports).
 *
 * `maxHeight` always reflects the actual available space. The flyout
 * component should still enforce its own CSS `min-height` if needed.
 */
const computeFlyoutPos = (
  triggerRect: DOMRect,
  flyoutHeight: number,
  { minWidth = 0, safetyMargin = 0 }: FlyoutPosOptions = {}
): FlyoutPos => {
  const spaceBelow = window.innerHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  const width = Math.max(triggerRect.width, minWidth);
  const left = triggerRect.left;

  const openUpward =
    spaceBelow < flyoutHeight &&
    spaceAbove > spaceBelow &&
    spaceAbove >= MIN_HEIGHT;
  const notEnoughSpace =
    !openUpward && spaceBelow < MIN_HEIGHT && spaceAbove < MIN_HEIGHT;

  const clampMaxHeight = (rawMaxHeight: number): number =>
    Math.max(0, rawMaxHeight - safetyMargin);

  if (notEnoughSpace) {
    const clampedHeight = Math.min(
      flyoutHeight,
      window.innerHeight - EDGE_GAP * 2
    );
    return {
      top: Math.round((window.innerHeight - clampedHeight) / 2),
      left,
      width,
      maxHeight: clampMaxHeight(clampedHeight),
    };
  }

  if (openUpward) {
    return {
      bottom: window.innerHeight - triggerRect.top + EDGE_GAP,
      left,
      width,
      maxHeight: clampMaxHeight(spaceAbove - EDGE_GAP),
    };
  }

  return {
    top: triggerRect.bottom + EDGE_GAP,
    left,
    width,
    maxHeight: clampMaxHeight(spaceBelow - EDGE_GAP),
  };
};

export default computeFlyoutPos;
